// pages/master-data/Route/MasterRoutePage.jsx
'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../components/DataTable';
import {
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress
} from '@mui/material';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useCompanyList } from '../../../stores/companyStore';
import { useRouteTripList, useRouteTripHistoryList, deleteRouteTrip, updateRouteTripAll } from '../../../stores/routeTripStore';

import ActionMenu from '../../../components/ActionMenu';
import { useNotifier } from '../../../components/Notifier';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DeletedRouteConfirmedDialog from './DeletedRouteConfirmedDialog';
import GuideRouteDialog from './GuideRouteDialog';
import { fleetApi } from '../../../api/axiosClient';

import MasterRouteAddEdit from './MasterRouteAddEdit';
import DetailPolygonList from './DetailPolygonList';
import DetailVehicleAssignList from './DetailVehicleAssignList';
import { useLanguage } from '../../../utils/i18n';

function MasterRoutePageContent() {
  const { t } = useLanguage();
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';

  const [activeTab, setActiveTab] = useState('active');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [detailPolygonOpen, setDetailPolygonOpen] = useState(false);
  const [selectedRoutePolygons, setSelectedRoutePolygons] = useState({ name: '', data: [] });

  const [detailVehicleOpen, setDetailVehicleOpen] = useState(false);
  const [selectedRouteVehicles, setSelectedRouteVehicles] = useState({ name: '', data: [] });

  const [infoOpen, setInfoOpen] = useState(false);

  const notify = useNotifier();
  const { companyOptions } = useCompanyList();

  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      const v = String(searchRaw ?? '').trim();
      setSearchValue(v);
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(t);
  }, [searchRaw]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPaginationModel({ page: 0, pageSize: 10 });
    setSearchRaw('');
    setSearchValue('');
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleCompanyChange = (options = []) => {
    const sanitized = (options || []).map((o) => ({ value: String(o.value), label: o.label ?? String(o.value) }));
    setSelectedCompanies(sanitized);
    try {
      if (typeof window !== 'undefined') {
        if (sanitized.length === 1) localStorage.setItem('companyId', String(sanitized[0].value));
        else localStorage.removeItem('companyId');
      }
    } catch {
      console.log('error saving company');
    }
  };

  const handleViewPolygons = (row) => {
    setSelectedRoutePolygons({
      name: row.name,
      data: row.spatials || []
    });
    setDetailPolygonOpen(true);
  };

  const handleViewVehicles = (row) => {
    setSelectedRouteVehicles({
      name: row.name,
      data: row.vehicles || []
    });
    setDetailVehicleOpen(true);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deletedRouteConfirmOpen, setDeletedRouteConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = () => {
    if (activeTab === 'history') {
      setConfirmOpen(false);
      setDeletedRouteConfirmOpen(true);
    } else {
      handleFinalDelete();
    }
  };

  const handleFinalDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteRouteTrip(confirmTargetId);
      notify({ message: t('notify.deleteSuccess', 'Deleted successfully'), severity: 'success' });
      setSearchValue(searchRaw + ' ');
    } catch (err) {
      console.error(err);
      notify({ message: t('notify.deleteFailed', 'Failed to delete data'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setDeletedRouteConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const handleSearch = ({ value }) => {
    setSearchRaw(value ?? '');
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const companyIdsStr = useMemo(() => selectedCompanies.map((c) => c.value).join(','), [selectedCompanies]);

  const {
    data: activeHookData,
    isLoading: activeHookLoading,
    totalRecord: activeHookTotal
  } = useRouteTripList(paginationModel.page, paginationModel.pageSize, 'name', searchValue, companyIdsStr);

  const {
    data: historyHookData,
    isLoading: historyHookLoading,
    totalRecord: historyHookTotal
  } = useRouteTripHistoryList(paginationModel.page, paginationModel.pageSize, 'name', searchValue, companyIdsStr);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const realData = activeTab === 'active' ? activeHookData || [] : historyHookData || [];
  const isLoadingRoutes = activeTab === 'active' ? activeHookLoading : historyHookLoading;
  const totalRecord = activeTab === 'active' ? activeHookTotal : historyHookTotal;

  const [updateAllOpen, setUpdateAllOpen] = useState(false);
  const [updateAllProgress, setUpdateAllProgress] = useState(0);
  const [updateAllTotal, setUpdateAllTotal] = useState(0);
  const [updateAllCurrentName, setUpdateAllCurrentName] = useState('');

  const handleUpdateRouteAll = async (row) => {
    try {
      const todayIso = new Date().toISOString();
      const payload = {
        name: row.name,
        company: row.company_id || row.company,
        spatials: (row.spatials || []).map((s) => ({
          spatial_id: s.spatial_id,
          spatial_name: s.spatial_name,
          spatial_type: s.spatial_type,
          sequence_number: s.sequence_number
        })),
        vehicles: (row.vehicles || []).map((v) => ({
          vehicle: v.vehicle?.id || v.vehicle?.vehicle_id || v.vehicle,
          valid_date: todayIso
        }))
      };

      await updateRouteTripAll(row.id, payload);
      notify({ message: 'Route updated successfully', severity: 'success' });
      setSearchValue(searchRaw + ' '); // trigger re-fetch
    } catch (err) {
      console.error(err);
      notify({ message: 'Failed to update route', severity: 'error' });
    }
  };

  const handleUpdateAllBatch = async () => {
    const toUpdate = realData.filter((r) => {
      const vehicles = r.vehicles || [];
      if (vehicles.length > 0) {
        return vehicles.some((v) => getDayLeft(v.valid_date || r.valid_date || r.created_date, r.created_date) <= 0);
      }
      return getDayLeft(r.valid_date || r.created_date, r.created_date) <= 0;
    });

    if (toUpdate.length === 0) {
      notify({ message: 'No routes need to be updated on this page.', severity: 'info' });
      return;
    }

    setUpdateAllTotal(toUpdate.length);
    setUpdateAllProgress(0);
    setUpdateAllOpen(true);

    for (let i = 0; i < toUpdate.length; i++) {
      const row = toUpdate[i];
      setUpdateAllCurrentName(row.name);
      setUpdateAllProgress(i + 1);

      try {
        const todayIso = new Date().toISOString();
        const payload = {
          name: row.name,
          company: row.company_id || row.company,
          spatials: (row.spatials || []).map((s) => ({
            spatial_id: s.spatial_id,
            spatial_name: s.spatial_name,
            spatial_type: s.spatial_type,
            sequence_number: s.sequence_number
          })),
          vehicles: (row.vehicles || []).map((v) => ({
            vehicle: v.vehicle?.id || v.vehicle?.vehicle_id || v.vehicle,
            valid_date: todayIso
          }))
        };
        await updateRouteTripAll(row.id, payload);
      } catch (err) {
        console.error('Failed to update', row.name, err);
      }
    }

    setUpdateAllOpen(false);
    notify({ message: `Successfully updated ${toUpdate.length} routes.`, severity: 'success' });
    setSearchValue(searchRaw + ' '); // trigger re-fetch
  };

  const getDayLeft = (validAtStr, createdAtStr) => {
    const startDateStr = validAtStr || createdAtStr;
    if (!startDateStr) return 0;
    const startDate = new Date(startDateStr);
    const expDate = new Date(startDate);
    expDate.setMonth(expDate.getMonth() + 1);
    const now = new Date();
    const diffTime = expDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecord > 0 ? totalRecord : 9999;
      let columns = [];

      if (companyIdsStr) {
        if (companyIdsStr.includes(',')) {
          columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: companyIdsStr });
        } else {
          columns.push({ name: 'company_id', logic_operator: '=', operator: 'AND', value: companyIdsStr });
        }
      }

      if (searchValue) {
        columns.push({ name: 'name', logic_operator: 'like', operator: 'AND', value: searchValue });
      }

      if (columns.length === 0) {
        columns.push({ logic_operator: 'like', name: 'name', operator: 'AND', value: '' });
      }

      // Add active filter for export
      columns.push({ name: 'is_active', logic_operator: '=', operator: 'AND', value: activeTab === 'active' ? 'true' : 'false' });

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [{ name: 'company', column_results: ['name'] }],
        columns: columns
      };

      const resp = await fleetApi.post('/route-trip/search', payload);
      const data = resp?.data ?? null;
      const fetchedRows = Array.isArray(data) ? data : (data?.data ?? []);

      return fetchedRows.map((route) => {
        const uniqueSpatials = [];
        const seenSeqs = new Set();
        (route.spatials || []).forEach((sp) => {
          if (!seenSeqs.has(sp.sequence_number)) {
            seenSeqs.add(sp.sequence_number);
            uniqueSpatials.push(sp);
          }
        });

        return {
          ...route,
          effective_date: route.valid_date || route.created_date,
          status: route.is_active,
          total_polygon: uniqueSpatials.length,
          total_vehicle: (route.vehicles || []).length
        };
      });
    } catch (err) {
      console.error('Route export all error', err);
      return [];
    }
  };

  const tableRows = useMemo(() => {
    if (!Array.isArray(realData)) return [];

    return realData.map((route) => {
      const uniqueSpatials = [];
      const seenSeqs = new Set();
      (route.spatials || []).forEach((sp) => {
        if (!seenSeqs.has(sp.sequence_number)) {
          seenSeqs.add(sp.sequence_number);
          uniqueSpatials.push(sp);
        }
      });

      return {
        ...route,
        effective_date: route.valid_date || route.created_date,
        status: route.is_active,
        total_polygon: uniqueSpatials.length,
        total_vehicle: (route.vehicles || []).length
      };
    });
  }, [realData]);

  const baseColumns = [
    {
      field: 'company',
      headerName: t('masterPit.company', 'Company'),
      flex: 1.5,
      valueGetter: (value, row) => row?.join?.company_name
    },
    { field: 'name', headerName: t('masterRoute.routeName', 'Route Name'), flex: 2 },
    {
      field: 'total_polygon',
      headerName: t('masterRoute.totalPolygons', 'Total Polygons'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      excludeFromExport: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        if (!r) return '0';
        const uniqueSpatials = [];
        const seenSeqs = new Set();
        (r.spatials || []).forEach((sp) => {
          if (!seenSeqs.has(sp.sequence_number)) {
            seenSeqs.add(sp.sequence_number);
            uniqueSpatials.push(sp.spatial_name || sp.name || sp.geofence_name || sp.pool_name || sp.transit_point_name || '-');
          }
        });
        if (uniqueSpatials.length === 0) return '0';
        return `${uniqueSpatials.length} (${uniqueSpatials.join(', ')})`;
      },
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <Typography variant="body2" fontWeight="bold">
            {params.row?.total_polygon || 0}
          </Typography>
          <Tooltip title={t('masterRoute.viewPolygons', 'View Polygons')}>
            <IconButton size="small" color="primary" onClick={() => handleViewPolygons(params.row)}>
              <TextSnippetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'polygons_export',
      headerName: t('masterRoute.polygons', 'Polygons'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        if (!r) return [];
        const uniqueSpatials = [];
        const seenSeqs = new Set();
        (r.spatials || []).forEach((sp) => {
          if (!seenSeqs.has(sp.sequence_number)) {
            seenSeqs.add(sp.sequence_number);
            uniqueSpatials.push(sp.spatial_name || sp.name || sp.geofence_name || sp.pool_name || sp.transit_point_name || '-');
          }
        });
        return uniqueSpatials;
      }
    },
    {
      field: 'total_vehicle',
      headerName: t('masterRoute.totalVehicles', 'Total Vehicles'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      excludeFromExport: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        if (!r) return '0';
        const vehicles = r.vehicles || [];
        if (vehicles.length === 0) return '0';
        const names = vehicles.map((v) => v.join?.vehicle_license_plate || v.join?.vehicle_name || v.license_plate || v.name || '-');
        return `${names.length} (${names.join(', ')})`;
      },
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <Typography variant="body2" fontWeight="bold">
            {params.row?.total_vehicle || 0}
          </Typography>
          <Tooltip title={t('masterRoute.viewVehicles', 'View Vehicles')}>
            <IconButton size="small" color="primary" onClick={() => handleViewVehicles(params.row)}>
              <TextSnippetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'vehicles_export',
      headerName: t('masterRoute.vehicles', 'Vehicles'),
      hide: true,
      valueGetter: (value, row) => {
        const r = row?.row ?? row;
        if (!r) return [];
        const vehicles = r.vehicles || [];
        return vehicles.map((v) => v.join?.vehicle_license_plate || v.join?.vehicle_name || v.license_plate || v.name || '-');
      }
    },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('masterCompany.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('masterCompany.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('masterCompany.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('masterCompany.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : [])
  ];

  const activeColumns = [
    ...baseColumns,
    {
      field: 'update_status',
      headerName: 'Update Status',
      flex: 1,
      renderCell: (params) => {
        const vehicles = params.row.vehicles || [];
        let needsUpdate = false;

        if (vehicles.length > 0) {
          needsUpdate = vehicles.some((v) => {
            const dl = getDayLeft(v.valid_date || params.row.valid_date || params.row.created_date, params.row.created_date);
            return dl <= 0;
          });
        } else {
          const dl = getDayLeft(params.row.valid_date || params.row.created_date, params.row.created_date);
          needsUpdate = dl <= 0;
        }

        if (needsUpdate) {
          return (
            <Button size="small" color="warning" variant="outlined" onClick={() => handleUpdateRouteAll(params.row)}>
              Update Route
            </Button>
          );
        }
        return (
          <Box display="flex" flexDirection="column" justifyContent="center" height="100%">
            <Typography variant="body2" color="success.main" fontWeight="bold">
              Up to Date
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  const historyColumns = [
    ...baseColumns,
    {
      field: 'updated_at',
      headerName: 'Updated At',
      flex: 1,
      valueGetter: (value, row) => row?.modified_date || row?.created_date || '-'
    },
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      width: 120,
      headerAlign: 'center',
      renderCell: (params) => <ActionMenu row={params.row} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
    }
  ];

  const columns = activeTab === 'active' ? activeColumns : historyColumns;

  const tableTitle = (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="h6">{t('masterRoute.masterRoute', 'Master Route')}</Typography>
    </Box>
  );

  return (
    <div>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Active Route" value="active" />
          <Tab label="History Route" value="history" />
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {activeTab === 'active' && (
            <Button variant="contained" color="warning" size="small" onClick={handleUpdateAllBatch} sx={{ fontWeight: 'bold' }}>
              Update All
            </Button>
          )}
          <IconButton onClick={() => setInfoOpen(true)} sx={{ bgcolor: '#e3f2fd', color: '#0374DA', borderRadius: 2 }} size="small">
            <InfoOutlinedIcon />
          </IconButton>
        </Box>
      </Box>

      <DataTable
        title={tableTitle}
        rows={tableRows}
        columns={columns}
        loading={isLoadingRoutes}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        rowCount={totalRecord || 0}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ value }) => handleSearch({ value })}
        onAddClick={handleAddClick}
        searchLabel={t('masterRoute.searchByRouteName', 'Search by Route Name')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
        onFetchAllForExport={handleFetchAllForExport}
      />

      <MasterRouteAddEdit
        open={openDialog}
        id={editingId}
        onClose={handleDialogClose}
        companyOptions={companyOptionsNormalized}
        tabMode={activeTab}
      />

      <DetailPolygonList
        open={detailPolygonOpen}
        onClose={() => setDetailPolygonOpen(false)}
        routeName={selectedRoutePolygons.name}
        polygons={selectedRoutePolygons.data}
      />

      <DetailVehicleAssignList
        open={detailVehicleOpen}
        onClose={() => setDetailVehicleOpen(false)}
        routeName={selectedRouteVehicles.name}
        vehicles={selectedRouteVehicles.data}
      />

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('general.confirmDelete', 'Confirm Delete')}
        message={t('masterRoute.confirmDeleteRoute', 'Are you sure you want to delete this route?')}
        confirmLabel={t('general.delete', 'Delete')}
        cancelLabel={t('general.cancel', 'Cancel')}
        itemName={confirmName}
      />

      <DeletedRouteConfirmedDialog
        open={deletedRouteConfirmOpen}
        loading={confirmLoading}
        onClose={() => setDeletedRouteConfirmOpen(false)}
        onConfirm={handleFinalDelete}
        routeName={confirmName}
      />

      <GuideRouteDialog open={infoOpen} onClose={() => setInfoOpen(false)} />

      <Dialog open={updateAllOpen} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>Updating Routes</DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Processing {updateAllProgress} of {updateAllTotal}...
          </Typography>
          <LinearProgress
            variant="determinate"
            value={updateAllTotal > 0 ? (updateAllProgress / updateAllTotal) * 100 : 0}
            sx={{ height: 8, borderRadius: 4, mb: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            Current: <strong>{updateAllCurrentName}</strong>
          </Typography>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MasterRoutePage() {
  return (
    <>
      <MasterRoutePageContent />
    </>
  );
}
